/**
 * CurrencyList Component
 *
 * This component renders a list of selectable currencies used within
 * the table footer features (e.g., Expand Sum mode).
 * It displays all available currencies from the Redux store and optionally
 * toggles visibility based on the expand-currency menu state.
 *
 * Props:
 *  - `currencyMenuHandler` (function, optional):
 *        A callback invoked when a currency item is clicked.
 *        Typically used to close the dropdown menu after a selection.
 *
 *  - `shouldExpandCurrencies` (boolean, optional):
 *        When true, the currency list is shown with the `"open"` CSS class.
 *        When false or undefined, the list is collapsed.
 *
 * State (via Redux):
 *  - `availableCurrencies`: Array of currency codes available for aggregation
 *        (e.g., ["USD", "EUR", "GBP"]). Retrieved from `state.stats.availableCurrencies`.
 *
 * Behavior:
 *  - The component automatically maps over all available currencies, rendering
 *    a `CurrencyItem` for each one.
 *  - Adds the `"open"` class when the dropdown is expanded, allowing the menu
 *    to be animated or styled differently.
 *  - Marks the first currency item with `isFirstItem={true}`, which can be
 *    useful for applying top-border overrides or rounded corners.
 *
 * Usage Example:
 *   <CurrencyList
 *     shouldExpandCurrencies={isOpen}
 *     currencyMenuHandler={handleToggle}
 *   />
 */

import React from "react";
import CurrencyItem from "./CurrencyItem";
import { useAppSelector } from "../../../../../../hook/useAppSelector";

interface Props {
  currencyMenuHandler?: () => void;
  shouldExpandCurrencies?: boolean;
}

const CurrencyList: React.FC<Props> = ({
  currencyMenuHandler,
  shouldExpandCurrencies,
}) => {
  const availableCurrencies = useAppSelector(
    (state) => state.stats.availableCurrencies
  );

  return (
    <ul className={`currency_list ${shouldExpandCurrencies ? "open" : ""}`}>
      {availableCurrencies.map((currency, index) => (
        <CurrencyItem
          key={currency}
          currencyMenuHandler={currencyMenuHandler}
          currencyItem={currency}
          isFirstItem={index === 0}
        />
      ))}
    </ul>
  );
};

export default CurrencyList;
